export function initArchiveLoadMore(): void {
  const container = document.getElementById('archive-container')
  if (!container) {
    return
  }

  const loadMoreBtn = document.getElementById('archive-load-more') as HTMLButtonElement | null
  const wrapper = document.getElementById('archive-load-more-wrapper')
  const filterBtns = document.querySelectorAll<HTMLButtonElement>('.archive-filter-btn')
  const clearBtn = document.getElementById('archive-clear-filters') as HTMLButtonElement | null
  const clearWrapper = document.getElementById('archive-clear-filters-wrapper')

  const archiveUrl =
    loadMoreBtn?.dataset.archiveUrl ||
    (document.getElementById('archive-filters') as HTMLElement | null)?.dataset.archiveUrl ||
    window.location.pathname

  const activeFilters = new Set<string>()
  filterBtns.forEach((btn) => {
    if (btn.hasAttribute('data-active') && btn.dataset.filter) {
      activeFilters.add(btn.dataset.filter)
    }
  })

  let currentPage = parseInt(loadMoreBtn?.dataset.currentPage || '1', 10)
  let maxPages = parseInt(loadMoreBtn?.dataset.maxPages || '1', 10)
  let isLoading = false

  const buildQueryString = (): string => {
    if (activeFilters.size === 0) {
      return ''
    }
    return '?filter=' + Array.from(activeFilters).join(',')
  }

  const buildFetchUrl = (page: number): string => {
    const base = archiveUrl.replace(/\/$/, '')
    const params = new URLSearchParams()

    if (activeFilters.size > 0) {
      params.set('filter', Array.from(activeFilters).join(','))
    }

    if (page > 1) {
      params.set('paged', page.toString())
    }

    const qs = params.toString()
    return `${base}/${qs ? '?' + qs : ''}`
  }

  const updateBrowserUrl = () => {
    const base = archiveUrl.replace(/\/$/, '')
    history.pushState({ filters: Array.from(activeFilters) }, '', base + '/' + buildQueryString())
  }

  const syncFilterUI = () => {
    filterBtns.forEach((btn) => {
      const key = btn.dataset.filter || ''
      const isActive = activeFilters.has(key)
      btn.toggleAttribute('data-active', isActive)
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false')

      const iconSpan = btn.querySelector<HTMLElement>('[data-filter-icon]')
      if (iconSpan) {
        iconSpan.classList.toggle('hidden', !isActive)
      }
    })

    if (clearWrapper) {
      clearWrapper.classList.toggle('hidden', activeFilters.size === 0)
    }
  }

  const setContainerLoading = (loading: boolean) => {
    container.style.transition = loading ? '' : 'opacity 0.2s ease-out'
    container.style.opacity = loading ? '0.4' : '1'
    container.style.pointerEvents = loading ? 'none' : ''
  }

  const setLoadMoreState = (loading: boolean) => {
    isLoading = loading
    if (!loadMoreBtn) {
      return
    }
    if (loading) {
      loadMoreBtn.setAttribute('disabled', 'true')
      loadMoreBtn.setAttribute('aria-busy', 'true')
      loadMoreBtn.textContent = 'Laddar...'
    } else {
      loadMoreBtn.removeAttribute('disabled')
      loadMoreBtn.removeAttribute('aria-busy')
      loadMoreBtn.textContent = 'Ladda fler'
    }
  }

  const updateLoadMoreVisibility = () => {
    if (!wrapper) {
      return
    }
    if (currentPage >= maxPages) {
      wrapper.style.display = 'none'
    } else {
      wrapper.style.removeProperty('display')
    }
  }

  const appendItems = (items: NodeListOf<Element>, animate: boolean) => {
    items.forEach((item, index) => {
      const el = item.cloneNode(true) as HTMLElement
      if (animate) {
        el.style.opacity = '0'
      }
      container.appendChild(el)
      if (animate) {
        setTimeout(() => {
          el.style.transition = 'opacity 0.3s ease-in-out'
          el.style.opacity = '1'
        }, index * 50)
      }
    })
  }

  const fetchPosts = async (page: number, replace: boolean): Promise<void> => {
    if (isLoading) {
      return
    }
    setLoadMoreState(true)
    setContainerLoading(true)

    try {
      const response = await fetch(buildFetchUrl(page), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`)
      }

      const newMaxPages = response.headers.get('X-Archive-Max-Pages')
      if (newMaxPages !== null) {
        maxPages = parseInt(newMaxPages, 10)
        if (loadMoreBtn) {
          loadMoreBtn.dataset.maxPages = newMaxPages
        }
      }

      const html = await response.text()
      const doc = new DOMParser().parseFromString(html, 'text/html')
      const newItems = doc.querySelectorAll('.archive-item')

      if (replace) {
        container.innerHTML = ''
      }

      setContainerLoading(false)
      appendItems(newItems, true)

      currentPage = page
      if (loadMoreBtn) {
        loadMoreBtn.dataset.currentPage = page.toString()
      }

      updateLoadMoreVisibility()
    } catch (error) {
      console.error('Failed to load archive posts:', error)
      setContainerLoading(false)
      if (!replace) {
        if (loadMoreBtn) {
          loadMoreBtn.textContent = 'Försök igen'
        }
        currentPage--
      }
    }

    setLoadMoreState(false)
  }

  window.addEventListener('popstate', (event) => {
    const state = event.state as { filters?: string[] } | null
    activeFilters.clear()

    if (state?.filters) {
      state.filters.forEach((f) => activeFilters.add(f))
    } else {
      const param = new URLSearchParams(window.location.search).get('filter') ?? ''
      if (param) {
        param.split(',').forEach((f) => activeFilters.add(f.trim()))
      }
    }

    syncFilterUI()
    fetchPosts(1, true)
  })

  loadMoreBtn?.addEventListener('click', () => {
    if (!isLoading && currentPage < maxPages) {
      fetchPosts(currentPage + 1, false)
    }
  })

  filterBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      if (isLoading) {
        return
      }

      const key = btn.dataset.filter || ''
      if (!key) {
        return
      }

      if (activeFilters.has(key)) {
        activeFilters.delete(key)
      } else {
        activeFilters.add(key)
      }

      syncFilterUI()
      updateBrowserUrl()
      fetchPosts(1, true)
    })
  })

  clearBtn?.addEventListener('click', () => {
    if (isLoading) {
      return
    }

    activeFilters.clear()
    syncFilterUI()
    updateBrowserUrl()
    fetchPosts(1, true)
  })
}
